import { z } from 'zod/v4';
import { FileSchema } from '../../files/index.js';
import { Currency, TagSchema } from '../../index.js';
import { PerformedBySchema } from '../../performed-by/index.js';
import { PropertySchema, } from '../../properties/index.js';
import { booleanSchema } from '../../util/schema.js';
import { queryJSONSchema } from '../../util/validation/index.js';
import { CreateFixedDistributionSchema, } from '../fixed-distributions/index.js';
import { VoucherCategory, VoucherTypeIds, VoucherTypeSetting, VoucherTypeSettingAndSettingDetailsSchema, } from '../vouchers/index.js';
export var CaseVoucherSource;
(function (CaseVoucherSource) {
    CaseVoucherSource["Creditor"] = "CREDITOR";
    CaseVoucherSource["Collector"] = "COLLECTOR";
    // ThridParty = 'THIRD_PARTY',
})(CaseVoucherSource || (CaseVoucherSource = {}));
export var State;
(function (State) {
    State["Unmapped"] = "UNMAPPED";
    State["Unknown"] = "UNKNOWN";
    State["Archived"] = "ARCHIVED";
    State["Overpayment"] = "OVERPAYMENT";
    State["FullPayment"] = "FULL_PAYMENT";
    State["PartialPayment"] = "PARTIAL_PAYMENT";
    State["PaymentPlanEntryPayment"] = "PAYMENT_PLAN_ENTRY_PAYMENT";
    State["UnkownPaymentPlanPayment"] = "UNKNOWN_PAYMENT_PLAN_PAYMENT";
    State["AccordPayment"] = "ACCORD_PAYMENT";
    State["PrincipalPayment"] = "PRINCIPAL_PAYMENT";
    State["Pending"] = "PENDING";
})(State || (State = {}));
// States where a deposit needs no further manual handling. A voucher in one of
// these states (or already mapped to a case) is excluded from deposit
// recognition and the transaction account mapping flow.
export const handledCaseVoucherStates = [
    State.Unknown,
    State.Archived,
    State.AccordPayment,
    State.FullPayment,
    State.Overpayment,
    State.PartialPayment,
    State.PaymentPlanEntryPayment,
    State.PrincipalPayment,
    State.UnkownPaymentPlanPayment,
];
export const isCaseVoucherHandled = (caseVoucher) => (caseVoucher.state != null &&
    handledCaseVoucherStates.includes(caseVoucher.state)) ||
    caseVoucher.caseId != null;
const BaseCaseVoucherSchema = z.object({
    source: z.enum(CaseVoucherSource),
    amount: z.number(),
    date: z.iso.datetime({ offset: true }),
    voucherTypeId: z.uuid(),
});
export var CardType;
(function (CardType) {
    CardType["Mastercard"] = "MASTERCARD";
    CardType["Visa"] = "VISA";
    CardType["VisaElectron"] = "VISA_ELECTRON";
    CardType["VisaDankort"] = "VISA_DANKORT";
    CardType["Dankort"] = "DANKORT";
})(CardType || (CardType = {}));
export var PaymentType;
(function (PaymentType) {
    PaymentType["BankTransfer"] = "BANK_TRANSFER";
    PaymentType["Mastercard"] = "MASTERCARD";
    PaymentType["Visa"] = "VISA";
    PaymentType["Dankort"] = "DANKORT";
    PaymentType["IBAN"] = "IBAN";
    PaymentType["Creditor"] = "CREDITOR";
    PaymentType["PayPal"] = "PAYPAL";
    PaymentType["ApplePay"] = "APPLE_PAY";
    PaymentType["GooglePay"] = "GOOGLE_PAY";
    // DK
    PaymentType["FIK"] = "FIK";
    PaymentType["Leverand\u00F8rservice"] = "LEVERAND\u00D8RSERVICE";
    PaymentType["Betalingsservice"] = "BETALINGSSERVICE";
    PaymentType["MobilePay"] = "MOBILEPAY";
    // SE
    PaymentType["Swish"] = "SWISH";
    // NO
    PaymentType["Vipps"] = "VIPPS";
})(PaymentType || (PaymentType = {}));
export var InterestPeriodDefaultBehavior;
(function (InterestPeriodDefaultBehavior) {
    InterestPeriodDefaultBehavior["KeepFrozen"] = "KEEP_FROZEN";
    InterestPeriodDefaultBehavior["Reapply"] = "REAPPLY";
})(InterestPeriodDefaultBehavior || (InterestPeriodDefaultBehavior = {}));
export const InterestPeriodSchema = z.object({
    id: z.uuid(),
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }).nullable(),
    rate: z.number().min(0),
    onDefault: z.enum(InterestPeriodDefaultBehavior),
    // Set when a Reapply period is voided; reapplied interest counts only
    // from this date.
    voidedAt: z.iso.datetime({ offset: true }).nullable(),
    paymentPlanId: z.uuid().nullable(),
    createdBy: PerformedBySchema.nullable(),
    createdAt: z.iso.datetime({ offset: true }),
});
export const AddInterestPeriodSchema = z.object({
    from: z.iso.datetime({ offset: true }),
    to: z.iso.datetime({ offset: true }).nullable(),
    rate: z.number().min(0),
    onDefault: z.enum(InterestPeriodDefaultBehavior),
    paymentPlanId: z.uuid().nullish(),
});
// Half-open rate windows [from, to); to === null means open-ended
export const interestPeriodWindowsOverlap = (a, b) => {
    const aFrom = new Date(a.from).getTime();
    const aTo = a.to !== null ? new Date(a.to).getTime() : null;
    const bFrom = new Date(b.from).getTime();
    const bTo = b.to !== null ? new Date(b.to).getTime() : null;
    return (aTo === null || bFrom < aTo) && (bTo === null || aFrom < bTo);
};
export const validateInterestPeriods = (periods) => {
    const seenIds = new Set();
    for (const period of periods) {
        if (seenIds.has(period.id)) {
            return `Duplicate interest period id (${period.id})`;
        }
        seenIds.add(period.id);
        const from = new Date(period.from).getTime();
        if (period.to !== null && from >= new Date(period.to).getTime()) {
            return `Interest period from (${period.from}) must be before to (${period.to})`;
        }
        if (period.voidedAt !== null &&
            new Date(period.voidedAt).getTime() < from) {
            return `Interest period voidedAt (${period.voidedAt}) cannot be before from (${period.from})`;
        }
    }
    const sorted = [...periods].sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());
    for (let i = 0; i < sorted.length - 1; i++) {
        if (interestPeriodWindowsOverlap(sorted[i], sorted[i + 1])) {
            return 'Interest periods cannot overlap';
        }
    }
    return null;
};
// Voiding a Reapply period on default makes what its lower rate waived due
// (D8). A rate above the voucher's own would have its excess taken back after
// the fact, which is not supported. A table's rate varies, so only a freeze
// can be reapplied there.
export const validateReapplyRates = (periods, voucher) => {
    const base = voucher.setting === VoucherTypeSetting.InterestFromTable
        ? 0
        : (voucher.interest ?? 0);
    const above = periods.find((period) => period.onDefault === InterestPeriodDefaultBehavior.Reapply &&
        period.rate > base);
    return above === undefined
        ? null
        : `An interest period reapplied on default cannot charge more than the voucher's own rate (${above.rate} > ${base})`;
};
export var DeleteType;
(function (DeleteType) {
    DeleteType["WorkflowRollback"] = "WORKFLOW_ROLLBACK";
    DeleteType["CaseMerge"] = "CASE_MERGE";
})(DeleteType || (DeleteType = {}));
export const LineItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    quantity: z.number(),
    price: z.number().int(),
    taxRate: z.number(),
    taxAmount: z.number(),
    netAmount: z.number().int(),
});
export const APIPatchCaseVoucherSchema = z.object({
    referenceId: z.string().optional(),
    dueDate: z.string().optional(),
    date: z.string().optional(),
    /** Null clears the override, falling the booking date back to `date` */
    bookingDate: z.iso.datetime({ offset: true }).nullish(),
    interest: z.number().min(0).nullish(),
    interestStartDate: z.string().optional(),
    interestEndDate: z.string().nullish(),
    interestPeriods: z.array(InterestPeriodSchema).nullish(),
    amount: z.number().int().nullish(),
    state: z.enum(State).nullish(),
    expired: z.boolean().optional(),
    irrecoverableDate: z.string().nullish(),
    expirationDate: z.string().nullish(),
    expirationManualSet: z.boolean().optional(),
    expirationFromDate: z.string().nullish(),
    /** Null: the creditor's or country's standard period for the category */
    expirationLength: z.number().int().positive().max(100).nullish(),
    appendix: FileSchema.nullish(),
    text: z.string().nullish(),
    /** Null unbooks non-deposit vouchers; booked deposits cannot be unbooked. */
    caseId: z.uuid().nullish(),
    referenceCaseVoucherId: z.uuid().nullish(),
    lineItems: z.array(LineItemSchema).nullish(),
});
export const PatchCaseVoucherSchema = APIPatchCaseVoucherSchema.extend({
    depositOnPaymentPlanEntryId: z.string().optional(),
    exported: z.boolean().optional(),
    details: z.any().optional(),
    cardType: z.union([z.enum(CardType), z.string(), z.null()]).optional(),
    paymentType: z.union([z.enum(PaymentType), z.string(), z.null()]).optional(),
    transactionAccountId: z.string().nullish(),
    caseVoucherId: z.string().optional(),
    associationId: z.string().nullish(),
});
export const MapCaseVoucherEntrySchema = z.union([
    z.object({
        caseVoucherId: z.uuid(),
        caseId: z.uuid(),
    }),
    z.object({
        caseVoucherId: z.uuid(),
        // Only manual resolution states: payment outcome states (FullPayment,
        // Overpayment, ...) are produced by deposit handling and must not be
        // settable directly — that would mark a voucher handled without the
        // corresponding financial side effects
        state: z.union([z.literal(State.Unknown), z.literal(State.Archived)]),
    }),
]);
// Each mapping runs the full voucher patch pipeline (deposit handling,
// billing, interest recalculation), so one request must stay comfortably
// within gateway timeouts. Clients batch by this same constant.
export const MAX_CASE_VOUCHER_MAPPINGS = 25;
export const MapCaseVouchersSchema = z.object({
    mappings: z
        .array(MapCaseVoucherEntrySchema)
        .min(1)
        .max(MAX_CASE_VOUCHER_MAPPINGS),
});
export const caseVoucherCategoryOrder = [
    VoucherCategory.PRINCIPAL,
    VoucherCategory.INTEREST,
    VoucherCategory.COST,
    VoucherCategory.COURTCOST,
    VoucherCategory.TRANSACTION,
];
export const CaseVoucherFilterSchema = z.object({
    getDeleted: booleanSchema.nullish(),
    missingCaseId: booleanSchema.nullish(),
    fromDate: z.iso.datetime({ offset: true }).optional(),
    toDate: z.iso.datetime({ offset: true }).optional(),
    /** Filters on the effective booking date, so vouchers without one match on `date` */
    fromBookingDate: z.iso.datetime({ offset: true }).optional(),
    toBookingDate: z.iso.datetime({ offset: true }).optional(),
    fromExpirationDate: z.iso.datetime({ offset: true }).optional(),
    toExpirationDate: z.iso.datetime({ offset: true }).optional(),
    fromIrrecoverableDate: z.iso.datetime({ offset: true }).optional(),
    toIrrecoverableDate: z.iso.datetime({ offset: true }).optional(),
    fromAmount: z.coerce.number().int().optional(),
    toAmount: z.coerce.number().int().optional(),
    expired: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    exported: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
    voucherTypeIds: queryJSONSchema(z.array(z.uuid()), 'voucherTypeIds need to be a list of uuids').optional(),
    currencies: queryJSONSchema(z.array(z.enum(Currency)), `currencies need to be a list of currencies (${Object.values(Currency)})`).optional(),
    sources: queryJSONSchema(z.array(z.enum(CaseVoucherSource)), `sources need to be a valid list of sources (${Object.values(CaseVoucherSource)})`).optional(),
    states: queryJSONSchema(z.array(z.enum(State)), `states need to be a valid list of states (${Object.values(State)})`).optional(),
    sortKey: z
        .enum([
        'id',
        'date',
        'text',
        'source',
        'amount',
        'currency',
        'expirationDate',
        'irrecoverableDate',
        'createdAt',
        'paymentType',
        'expired',
        'exported',
    ])
        .optional(),
    creditorId: z.uuid().nullish(),
    sortDirection: z.enum(['ASC', 'DESC']).optional(),
});
const BaseCreateCaseVoucherSchema = z.object({
    source: z.enum(CaseVoucherSource),
    currency: z.enum(Currency).optional(),
    amount: z.number().int(),
    date: z.string(),
    voucherTypeId: z.uuid(),
    caseId: z.uuid().nullish(),
    caseVoucherId: z.uuid().nullish(),
    dueDate: z.string().nullish(),
    /** Defaults to `date` when not set — see effectiveBookingDate */
    bookingDate: z.iso.datetime({ offset: true }).nullish(),
    interest: z.number().nullish(),
    interestStartDate: z.string().nullish(),
    interestEndDate: z.string().nullish(),
    interestPeriods: z.array(InterestPeriodSchema).nullish(),
    appendix: FileSchema.nullish(),
    createdAt: z.string().nullish(),
    text: z.string().nullish(),
    tags: z.array(TagSchema).optional(),
    referenceId: z.string().trim().nullish(),
    importId: z.uuid().nullish(),
    referenceCaseVoucherId: z.uuid().nullish(),
    createdBy: PerformedBySchema.nullish(),
    details: z.any().optional(),
    lineItems: z.array(LineItemSchema).nullish(),
    transactionAccountId: z.uuid().nullish(),
    state: z.enum(State).nullish(),
    expirationFromDate: z.string().nullish(),
    properties: z.any().nullish(),
    uniqueId: z.string().nullish(),
    paymentType: z.union([z.enum(PaymentType), z.string(), z.null()]).optional(),
    associationId: z.string().nullish(),
    fixedDistributions: z.array(CreateFixedDistributionSchema).nullish(),
});
export const CreateCaseVoucherSchema = BaseCreateCaseVoucherSchema.extend({
    referenceCaseVouchers: z.lazy(() => z.array(CreateCaseVoucherSchema).optional()),
});
// What the voucher editor adds on top of the voucher for the side effects its
// type's EditorSettings ask for (payout to a contact, creditor disbursement)
export const CaseVoucherEditorInputSchema = z.object({
    contactId: z.uuid().nullish(),
    billCreditor: z.boolean().nullish(),
});
export const CaseVoucherSchema = z.intersection(z.intersection(BaseCaseVoucherSchema, VoucherTypeSettingAndSettingDetailsSchema), z.object({
    id: z.number(),
    caseId: z.string().nullable(),
    currency: z.enum(Currency),
    caseVoucherId: z.uuid(),
    appendix: FileSchema.nullable(),
    tenantId: z.uuid(),
    createdAt: z.iso.datetime({ offset: true }),
    text: z.string().nullable(),
    tags: z.array(TagSchema),
    referenceId: z.string().nullable(),
    importId: z.string().nullable(),
    referenceCaseVoucherId: z.string().nullable(),
    interest: z.number().nullable(),
    createdBy: PerformedBySchema.nullable(),
    interestStartDate: z.iso.datetime({ offset: true }).nullable(),
    interestEndDate: z.iso.datetime({ offset: true }).nullable(),
    interestPeriods: z.array(InterestPeriodSchema).nullish(),
    details: z.any().nullable(), // TODO: Fix type
    dueDate: z.iso.datetime({ offset: true }).nullable(),
    // The date the deposit is booked in the accounts. `date` is when the money
    // was received and is what interest is calculated from; the two differ when
    // the money is received in one period and booked in a later one. Null means
    // they are the same — use effectiveBookingDate to resolve it.
    bookingDate: z.iso.datetime({ offset: true }).nullish(),
    lineItems: z.array(LineItemSchema).nullable(),
    transactionAccountId: z.string().nullable(),
    transactionImportId: z.string().nullable(),
    state: z.enum(State).nullable(),
    originalAmount: z.number().optional(),
    // Voucher type
    category: z.string(),
    label: z.string(),
    expired: z.boolean(),
    expirationManualSet: z.boolean(),
    expirationDate: z.string().nullable(),
    expirationFromDate: z.string().nullable(),
    // Limitation period in whole years, counted from expirationFromDate ||
    // date and restarted by deposits. Copied from the type's
    // defaultExpirationLength on creation. Null: the creditor's or country's
    // standard period for the category.
    expirationLength: z.number().int().nullable(),
    irrecoverableDate: z.string().nullable(),
    deletedAt: z.string().nullable(),
    deletedBy: PerformedBySchema.nullable(),
    exported: z.boolean(),
    depositOnPaymentPlanEntryId: z.string().nullish(),
    cardType: z.union([z.enum(CardType), z.string(), z.null()]),
    paymentType: z.union([z.enum(PaymentType), z.string(), z.null()]),
    paymentMethodId: z.string().nullable(),
    properties: z.array(PropertySchema).optional(),
    associationId: z.string().nullable(),
    associationSourceCaseVoucherId: z.string().nullish(),
}));
// Engine 1 cannot hold a voucher that is only partly written off.
export const ErrorCannotMoveWrittenOffVoucher = 'Cannot move written-off debt to a case on the old economy engine';
// A voucher's amounts (design doc §10.3): what was entered (`originalAmount`;
// calculated interest has none, as its amount is calculated), what engine 2
// wrote off beside it, and what counts, `amount`: the entered amount less what
// was written off.
// Engine 2 stores write-offs beside the amount. Engine 1 vouchers, and engine
// 2's joint mirrors, which have nothing to write off, leave the columns null.
export const hasWriteOffAmounts = (voucher) => voucher.expiredAmount != null;
export const enteredAmount = (voucher) => voucher.originalAmount ?? voucher.amount;
export const writtenOffAmount = (voucher) => (voucher.expiredAmount ?? 0) + (voucher.irrecoverableAmount ?? 0);
// What counts of a voucher about to be written: engine 1 still drops expired
// and irrecoverable vouchers in postProcessing rather than here.
export const netAmount = (voucher) => voucher.originalAmount === undefined
    ? voucher.amount
    : voucher.originalAmount + writtenOffAmount(voucher);
/**
 * The date a deposit belongs on in the accounts. Deposits that are booked in
 * the same period as they are received — bank transfers and FIK payments,
 * almost always — carry no booking date of their own and fall back to `date`.
 */
export const effectiveBookingDate = (caseVoucher) => caseVoucher.bookingDate ?? caseVoucher.date;
export const isAssociationMirror = (voucher) => voucher.associationSourceCaseVoucherId != null &&
    voucher.associationSourceCaseVoucherId !== voucher.caseVoucherId;
// A voucher the association sync owns: the server rejects direct edits to it,
// so callers must not offer them. Joint deposits are always generated, which
// also covers legacy mirrors created before associationSourceCaseVoucherId.
export const isAssociationManaged = (voucher) => isAssociationMirror(voucher) ||
    voucher.voucherTypeId === VoucherTypeIds.JOINTDEPOSIT;
export const INCOMING_PAYMENT_STATES = [
    State.FullPayment,
    State.PartialPayment,
    State.Overpayment,
    State.PaymentPlanEntryPayment,
    State.UnkownPaymentPlanPayment,
    State.AccordPayment,
    State.PrincipalPayment,
];
//# sourceMappingURL=index.js.map